import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { getIndividualList, getIndividualSumary } from '../../../api/statements';
import { InfoLabel, Table } from '../../../common/components';
import { useSetLoading } from '../../../common/hooks/appearance';
import {
  individualSummaryTableColumns,
  individualTableColumns,
  reports,
} from '../../../utils/constant';
import { formatDate, getPrice } from '../../../utils/formatters';
import { getModifiedIndividualData } from '../../../utils/utils';
import DownloadModal from '../../reports/downloadModal/downloadModal';
import EmailModal from '../../reports/emailModal/emailModal';
import ItemEmptyTile from '../../reports/item-empty-tile/item-empty-tile';
import IndividualStatementRow from '../statementRow';

const DetailedTable = ({ rowData, payload, setShowDetailedTable }) => {
  const { onePortalId, purchaserName } = rowData;
  const {
    startDate,
    endDate,
    timezone,
    entity,
    departments,
    businessUnit,
    salesManagers,
    unitManagers,
  } = payload;

  const [data, setData] = useState([]);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const showLoading = useMemo(() => loading, [loading]);
  const [nextData, setNextData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [totalDownpayment, setTotalDownpayment] = useState(null);
  const [_addendumInProgress, setAddendumInProgress] = useState(false);
  const [addendumNotes, setAddendumNotes] = useState(null);
  const [apdNote, setAPDNote] = useState(null);

  useSetLoading(showLoading);
  const toggleEmailModal = () => {
    setIsEmailModalOpen(!isEmailModalOpen);
  };
  const toggleDownloadModal = () => {
    setIsDownloadModalOpen(!isDownloadModalOpen);
  };
  const individualPayload = {
    startDate,
    endDate,
    timezone,
    entity,
    departments,
    businessUnit,
    salesManagers,
    unitManagers,
    opiOrName: onePortalId,
  };
  const generateReport = async () => {
    try {
      setLoading(true);
      const response = await getIndividualList(individualPayload);
      const individualData = getModifiedIndividualData(response?.data?.data);
      const summaryResponse = await getIndividualSumary(individualPayload);
      setSummary(summaryResponse?.data?.data?.summaryReport[0]);
      setNextData(summaryResponse?.data?.data?.nextReport);
      setAddendumInProgress(summaryResponse?.data?.data?.isAddendumInProgress);
      setData(individualData);
      setTotalDownpayment(response?.data?.totalDownpayment);
      setLoading(false);
      setAddendumNotes(response?.data?.addendumNotes);
      setAPDNote(response?.data?.financeWithAPDNote);
    } catch (_err) {
      setLoading(false);
    }
  };
  useEffect(() => {
    generateReport();
  }, []);
  const renderButtons = () => {
    return (
      <>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
          className="row toolbar-row"
        >
          <div className="cursor-pointer" onClick={() => setShowDetailedTable(false)}>
            <i className="fas fa-solid fa-arrow-left" />
            <span style={{ marginLeft: '5px' }}>
              Bulk Statement / {onePortalId} / {purchaserName}
            </span>
          </div>
          <div className={data && data.length > 0 ? 'cursor-default pointer' : 'cursor-default'}>
            <InfoLabel
              label="Email Report"
              iconLocation="Left"
              showIcon
              iconType="MdOutlineEmail"
              onClick={toggleEmailModal}
            />

            <div className="hr-r" />

            <InfoLabel
              label="Download Report"
              iconLocation="Left"
              showIcon
              iconType="FiDownload"
              onClick={toggleDownloadModal}
            />
          </div>
        </div>
        <hr />
      </>
    );
  };
  const renderModals = () => {
    return (
      <>
        <EmailModal
          isOpen={isEmailModalOpen}
          toggle={toggleEmailModal}
          reportType={reports.INDIVIDUAL}
          apiValues={{
            startDate,
            endDate,
            opiOrName: onePortalId,
          }}
          newFilters
        />
        <DownloadModal
          isOpen={isDownloadModalOpen}
          toggle={toggleDownloadModal}
          reportType={reports.INDIVIDUAL}
          urlParams={{
            startDate,
            endDate,
            opiOrName: onePortalId,
          }}
          newFilters
        />
      </>
    );
  };
  const renderSummaryTable = () => {
    return (
      <Table headers={individualSummaryTableColumns} bordered responsive className="simple-table">
        <tbody>
          <tr className="table-body-tr">
            <td>{getPrice(summary?.current)}</td>
            <td>{getPrice(summary?.day30) || '-'}</td>
            <td>{getPrice(summary?.day60) || '-'}</td>
            <td>{getPrice(summary?.day90) || '-'}</td>
            <td>{getPrice(summary?.day120) || '-'}</td>
            <td>{getPrice(summary?.above120) || '-'}</td>
          </tr>
        </tbody>
      </Table>
    );
  };

  const renderBottomDetails = () => {
    return (
      <div className="bottomDiv">
        <div className="nextPayment">
          {nextData?.paymentDueUponReceipt ? (
            <span className="nextPayment-text1">Payment due upon Receipt</span>
          ) : (
            <div>
              <span className="nextPayment-text1">
                Next Payment due : {formatDate(nextData?.expectedPaymentDate)}
              </span>
              <span className="nextPayment-text1-amount">
                {getPrice(nextData?.expectedPaymentAmount)}
              </span>
            </div>
          )}
        </div>
        {(addendumNotes || apdNote) && (
          <div className="noteDiv">
            {apdNote && <p className="addendumText">Note: {apdNote}</p>}
            {addendumNotes && <p className="addendumText">Note: {addendumNotes}</p>}

            <hr />
          </div>
        )}
        <div className="bottomDetail">
          <span>
            Please Return this portion with your payment and reference your
            <br />
            account number on your check or money order
          </span>
        </div>
      </div>
    );
  };
  return (
    <>
      {renderModals()}
      {renderButtons()}
      {data && data.length > 0 ? (
        <>
          <Table headers={individualTableColumns} bordered responsive className="dashboard-table">
            <tbody>
              {data?.map((rowItemData, rowNumber) => (
                <IndividualStatementRow key={rowNumber} rowData={rowItemData} id={rowNumber + 1} />
              ))}
            </tbody>
          </Table>
          <div className="nextPayment">
            <span className="nextPayment-text1">
              Note: Total Down Payment {getPrice(totalDownpayment)}
            </span>
          </div>
        </>
      ) : (
        !loading && <ItemEmptyTile />
      )}
      {data && data.length > 0 && summary && renderSummaryTable()}
      {data && data.length > 0 && nextData && renderBottomDetails()}
    </>
  );
};

DetailedTable.propTypes = {
  rowData: PropTypes.shape({
    onePortalId: PropTypes.string,
    purchaserName: PropTypes.string,
  }).isRequired,
  payload: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    timezone: PropTypes.string,
    entity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    salesManagers: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    unitManagers: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    departments: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    businessUnit: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  setShowDetailedTable: PropTypes.func.isRequired,
};

export default DetailedTable;
